import {
  flushPersistenceBeforeReload,
  type PersistenceBarrierResult,
} from "./persistenceReloadBarrier";
import {
  revalidateStaleBuildTarget,
  type StaleBuildRevalidation,
} from "./staleClientGuard";

const UPDATE_ATTEMPT_KEY = "lnpro:update-reload-attempt";
const UPDATE_RECEIPT_KEY = "lnpro:update-reload-receipt";

export type UpdateReloadDecision =
  | { status: "reload-committed"; barrier: PersistenceBarrierResult }
  | { status: "workspace-blocked"; barrier: PersistenceBarrierResult }
  | { status: "already-attempted" }
  | { status: "attempt-storage-unavailable" }
  | { status: Exclude<StaleBuildRevalidation, "confirmed"> };

type Revalidate = (targetSignature: string) => Promise<StaleBuildRevalidation>;
type Flush = () => PersistenceBarrierResult;

/**
 * Prepare one operator-approved reload without yielding after the persistence
 * barrier. The supplied commit calls window.location.reload synchronously.
 */
export async function prepareUpdateReload(
  targetSignature: string,
  options: {
    revalidate?: Revalidate;
    flush?: Flush;
    storage?: Storage;
    commitReload: () => void;
  }
): Promise<UpdateReloadDecision> {
  const revalidate = options.revalidate ?? revalidateStaleBuildTarget;
  const flush = options.flush ?? flushPersistenceBeforeReload;
  const storage = options.storage ?? window.sessionStorage;
  const revalidation = await revalidate(targetSignature);
  if (revalidation !== "confirmed") return { status: revalidation };

  const barrier = flush();
  if (!barrier.safe) return { status: "workspace-blocked", barrier };

  try {
    if (storage.getItem(UPDATE_ATTEMPT_KEY) === targetSignature) {
      return { status: "already-attempted" };
    }
    storage.setItem(UPDATE_ATTEMPT_KEY, targetSignature);
    if (storage.getItem(UPDATE_ATTEMPT_KEY) !== targetSignature) {
      return { status: "attempt-storage-unavailable" };
    }
    const receipt = JSON.stringify({
      targetSignature,
      participantCount: barrier.receipts.length,
      dirtyParticipantCount: barrier.receipts.filter((item) => item.dirty).length,
      durableDirtyParticipantCount: barrier.receipts.filter(
        (item) => item.dirty && item.durable
      ).length,
      mutationEpoch: barrier.mutationEpoch,
    });
    storage.setItem(UPDATE_RECEIPT_KEY, receipt);
    if (storage.getItem(UPDATE_RECEIPT_KEY) !== receipt) {
      return { status: "attempt-storage-unavailable" };
    }
  } catch {
    return { status: "attempt-storage-unavailable" };
  }

  // No await/yield may occur between the exact barrier receipt and navigation:
  // a passive effect or queued microtask could otherwise make new dirty work.
  options.commitReload();
  return { status: "reload-committed", barrier };
}

export const __testing = { UPDATE_ATTEMPT_KEY, UPDATE_RECEIPT_KEY };
